import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import HammLogo from "./HammLogo";
import { useT } from "@/i18n/LocaleContext";
import { useHoliday } from "@/context/HolidayContext";
import HolidayBanner from "@/components/holidays/HolidayBanner";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { t, locale } = useT();
  const { active } = useHoliday();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const headerRef = useRef(null);

  // Header-Höhe an CSS-Variable exposen, damit Hero/Anfrage-Portal-Seite den korrekten Top-Offset kennt.
  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const update = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, [active]);

  const INQUIRY_PATH = locale === "en" ? "/en/inquiry-portal" : "/anfrage-portal";

  const LINKS = [
    [t("nav.hygiene"), "hygiene", "nav-link-hygiene"],
    [t("nav.industries"), "branchen", "nav-link-industries"],
    [t("nav.services"), "leistungen", "nav-link-leistungen"],
    [t("nav.standards"), "normen", "nav-link-normen"],
    [t("nav.machines"), "maschinenpark", "nav-link-maschinenpark"],
  ];

  const go = (id) => { setOpen(false); scrollTo(id); };
  const switchTo = (target) => { setOpen(false); navigate(target); };

  return (
    <header ref={headerRef} className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E3E8EC]" data-testid="main-nav">
      <div className="relative bg-[var(--ci-blue)] text-white" data-testid="topbar-contact">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-9 flex items-center justify-end gap-5 sm:gap-7 font-tech text-[10px] sm:text-[11px] tracking-[0.14em] uppercase">
          <a href="tel:+4928319901500" data-testid="topbar-phone"
            className="flex items-center gap-2 hover:text-[var(--ci-turquoise)] transition-colors">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/></svg>
            <span className="hidden sm:inline text-white/70 tracking-[0.16em]">Unverbindliche Anfrage:</span>
            <span className="font-medium">+49 2831 9901 500</span>
          </a>
          <span aria-hidden="true" className="h-3 w-px bg-white/25" />
          <a href="mailto:verkauf@hamm.gmbh" data-testid="topbar-email"
            aria-label="E-Mail an verkauf@hamm.gmbh senden"
            className="flex items-center gap-2 hover:text-[var(--ci-turquoise)] transition-colors">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <span>Per Mail anfragen</span>
          </a>
        </div>
      </div>
      <HolidayBanner active={active} />
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 h-[76px] flex items-center justify-between">
        <button onClick={() => { setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="relative z-10 flex items-center nav-logo" data-testid="nav-logo" aria-label={locale === "en" ? "Back to home" : "Zur Startseite"}>
          <HammLogo />
        </button>
        <nav className="hidden lg:flex items-center gap-8 relative z-10">
          {LINKS.map(([label, id, tid]) => (
            <button key={id} onClick={() => scrollTo(id)} data-testid={tid}
              className="font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors duration-200">
              {label}
            </button>
          ))}
          <Link to={INQUIRY_PATH} data-testid="nav-link-anfrage"
            className="font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)] transition-colors duration-200">
            {t("nav.inquiry")}
          </Link>
          <Link to="/karriere" data-testid="nav-link-karriere"
            className="font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-blue)] hover:text-[var(--ci-turquoise)] transition-colors duration-200">
            {t("nav.career")}
          </Link>
        </nav>
        <div className="flex items-center gap-3 relative z-10">
          <div className="hidden sm:flex items-center border border-[#E3E8EC] rounded-full overflow-hidden" data-testid="lang-switcher" aria-label={t("nav.langLabel")}>
            <button onClick={() => switchTo("/")} data-testid="lang-de"
              aria-pressed={locale === "de"}
              className={`px-3 h-10 font-tech text-[10px] tracking-[0.18em] uppercase transition-colors duration-200 ${locale === "de" ? "bg-[var(--ci-blue)] text-white" : "text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)]"}`}>
              DE
            </button>
            <button onClick={() => switchTo("/en")} data-testid="lang-en"
              aria-pressed={locale === "en"}
              className={`px-3 h-10 font-tech text-[10px] tracking-[0.18em] uppercase transition-colors duration-200 ${locale === "en" ? "bg-[var(--ci-blue)] text-white" : "text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)]"}`}>
              EN
            </button>
          </div>
          <Link to={INQUIRY_PATH} data-testid="nav-cta-btn"
            className="hidden md:flex items-center gap-2 px-5 h-10 rounded-full bg-[var(--ci-blue)] hover:bg-[var(--ci-turquoise)] text-white font-tech text-[11px] tracking-[0.18em] uppercase whitespace-nowrap transition-colors duration-200 active:scale-[0.98]">
            {t("nav.cta")} <ArrowUpRight size={14} />
          </Link>
          <button onClick={() => setOpen(!open)} data-testid="nav-burger-btn" aria-label={locale === "en" ? "Open menu" : "Menü öffnen"} aria-expanded={open}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border border-[#E3E8EC] text-[var(--ci-grey)] hover:border-[var(--ci-turquoise)] transition-colors duration-200">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden border-t border-[#E3E8EC] bg-white px-6 py-4 flex flex-col" data-testid="mobile-nav">
          {LINKS.map(([label, id, tid]) => (
            <button key={id} onClick={() => go(id)} data-testid={`mobile-${tid}`}
              className="text-left py-3.5 border-b border-[#EEF1F4] font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-grey)] hover:text-[var(--ci-blue)] transition-colors duration-200">
              {label}
            </button>
          ))}
          <Link to={INQUIRY_PATH} onClick={() => setOpen(false)} data-testid="mobile-nav-link-anfrage"
            className="text-left py-3.5 border-b border-[#EEF1F4] font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-grey)]">
            {t("nav.inquiry")}
          </Link>
          <Link to="/karriere" onClick={() => setOpen(false)} data-testid="mobile-nav-link-karriere"
            className="text-left py-3.5 border-b border-[#EEF1F4] font-tech text-[11px] tracking-[0.22em] uppercase text-[var(--ci-blue)]">
            {t("nav.career")}
          </Link>
          <div className="mt-4 flex items-center border border-[#E3E8EC] rounded-full overflow-hidden self-start" data-testid="mobile-lang-switcher">
            <button onClick={() => switchTo("/")} data-testid="mobile-lang-de"
              className={`px-4 h-10 font-tech text-[10px] tracking-[0.18em] uppercase ${locale === "de" ? "bg-[var(--ci-blue)] text-white" : "text-[var(--ci-grey)]"}`}>DE</button>
            <button onClick={() => switchTo("/en")} data-testid="mobile-lang-en"
              className={`px-4 h-10 font-tech text-[10px] tracking-[0.18em] uppercase ${locale === "en" ? "bg-[var(--ci-blue)] text-white" : "text-[var(--ci-grey)]"}`}>EN</button>
          </div>
          <Link to={INQUIRY_PATH} onClick={() => setOpen(false)} data-testid="mobile-nav-cta-btn"
            className="mt-4 flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-[var(--ci-blue)] text-white font-tech text-[11px] tracking-[0.18em] uppercase whitespace-nowrap active:scale-[0.98]">
            {t("nav.cta")} <ArrowUpRight size={14} />
          </Link>
        </nav>
      )}
    </header>
  );
}
